/**
 * `TaskRunnerModal` — generic stepper modal for guided tasks (M3 Phase D).
 *
 * Owns the runtime state machine for a single `TaskDefinition`:
 *   - current step index
 *   - shared `TaskContext` bag (mutated by step components)
 *   - "Next button ready" gating, set by the current step
 *
 * Renders a header (title / "Step N of M" / progress bar), a body
 * delegating to the current step's `Component`, and a footer with
 * Back / Cancel / Next (or Finish on the final step).
 *
 * Styling matches `OutletConfirmModal` + `TilePickerModal` (dark Reef
 * theme, rounded panel, amber accent for primary actions). Clicking
 * the backdrop closes the modal only when no async work is pending.
 *
 * The runner intentionally has no concept of validation beyond the
 * boolean `ready` flag — each step owns its own form validation and
 * error display.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import type { TaskDefinition, TaskContext } from './types';

export interface TaskRunnerModalProps {
  open: boolean;
  task: TaskDefinition;
  onClose: () => void;
  /** Optional initial context (e.g. preselected device id). */
  initialContext?: TaskContext;
  /** Called when the user clicks Finish on the last step. */
  onComplete?: (context: TaskContext) => void;
}

export function TaskRunnerModal(props: TaskRunnerModalProps): JSX.Element | null {
  const { open, task, onClose, initialContext, onComplete } = props;

  const [stepIndex, setStepIndex] = useState(0);
  const [context, setContext] = useState<TaskContext>(() => initialContext ?? {});
  const [stepReady, setStepReady] = useState(false);

  // Reset whenever the modal re-opens with a different task.
  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    setContext(initialContext ?? {});
    setStepReady(false);
    // We intentionally re-run when `task.id` changes (different task)
    // or `open` flips from false→true. Other deps would re-fire on
    // every parent render — `eslint-plugin-react-hooks` isn't wired
    // into this workspace so the omission is documented here instead
    // of suppressed with a directive.
  }, [open, task.id]);

  const totalSteps = task.steps.length;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const currentStep = task.steps[stepIndex];

  const advance = useCallback((): void => {
    setStepIndex((i) => {
      if (i >= totalSteps - 1) return i;
      // Reset readiness — the next step will mark itself ready when
      // its preconditions are met.
      setStepReady(false);
      return i + 1;
    });
  }, [totalSteps]);

  const back = useCallback((): void => {
    setStepIndex((i) => Math.max(0, i - 1));
    setStepReady(false);
  }, []);

  const finish = useCallback((): void => {
    onComplete?.(context);
    onClose();
  }, [context, onClose, onComplete]);

  const progressPct = useMemo(
    () => Math.round(((stepIndex + 1) / totalSteps) * 100),
    [stepIndex, totalSteps],
  );

  if (!open || !currentStep) return null;

  const StepBody = currentStep.Component;
  const nextLabel =
    currentStep.nextLabel ?? (isLast ? 'Finish' : 'Next');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={task.title}
      data-testid="task-runner-modal"
      data-task-id={task.id}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl border border-reef-700 bg-reef-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-reef-700">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-reef-100 truncate">
                {task.title}
              </h2>
              <p className="text-xs text-reef-400 mt-0.5">
                Step {stepIndex + 1} of {totalSteps} —{' '}
                <span className="text-reef-200">{currentStep.title}</span>
              </p>
              {currentStep.subtitle ? (
                <p className="text-xs text-reef-500 mt-1">
                  {currentStep.subtitle}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="Close task"
              onClick={onClose}
              className="rounded-md px-2 py-1 text-reef-300 hover:text-reef-100 hover:bg-reef-800 transition shrink-0"
              data-testid="task-runner-close"
            >
              ✕
            </button>
          </div>
          {/* Progress bar */}
          <div
            className="mt-3 h-1 w-full rounded-full bg-reef-800 overflow-hidden"
            data-testid="task-runner-progress"
          >
            <div
              className="h-full bg-amber-400 transition-[width] duration-300 ease-out"
              style={{ width: `${progressPct}%` }}
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
            />
          </div>
        </header>

        {/* Body — current step */}
        <div
          className="flex-1 overflow-y-auto px-5 py-5"
          data-testid={`task-runner-step-${currentStep.id}`}
        >
          <StepBody
            context={context}
            setContext={setContext}
            setReady={setStepReady}
            advance={advance}
          />
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between gap-2 px-5 py-4 border-t border-reef-700">
          <button
            type="button"
            onClick={back}
            disabled={isFirst}
            className={clsx(
              'rounded-md border px-4 py-2 text-sm transition',
              isFirst
                ? 'border-reef-800 bg-reef-900 text-reef-600 cursor-not-allowed'
                : 'border-reef-700 bg-reef-800 text-reef-200 hover:bg-reef-700 hover:text-reef-100',
            )}
            data-testid="task-runner-back"
          >
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-reef-700 bg-reef-800 px-4 py-2 text-sm text-reef-200 hover:bg-reef-700 hover:text-reef-100 transition"
              data-testid="task-runner-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={isLast ? finish : advance}
              disabled={!stepReady}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium transition',
                stepReady
                  ? 'bg-amber-500 text-reef-950 hover:bg-amber-400'
                  : 'bg-reef-800 text-reef-500 cursor-not-allowed',
              )}
              data-testid="task-runner-next"
            >
              {nextLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}